import { API_METHODS } from '@compass/client/constants/api-methods';
import { useStore } from '@store/index';

import { ANALYTICS_DISABLED_ORG_IDS } from '@/constants/feature-flags';
import { useIs100msUser } from '@/hooks/useIs100msUser';
import { usePermissionMap } from '@/hooks/usePermissions';

const hasAnalyticsPagePermissions = (permissions?: Record<string, boolean>): boolean => {
  if (!permissions) return false;

  // Analytics read is the only requirement: the analytics-only role
  // (qc-analytics-viewer) grants just this and must be able to open the page.
  // A QC-surface permission is NOT required — drilldowns into QC are gated
  // separately (see CallBreakdownGrid), so lacking them just hides those links.
  return permissions[API_METHODS.AnalyticsService.GetOrgAnalytics] === true;
};

/**
 * Hook to check if the Analytics page should be shown and directly accessible.
 * This is a UI affordance only: analytics APIs still enforce org/program
 * permissions server-side via the backend auth annotations.
 *
 * Access rules (mirrors useShouldShowQcPage):
 *   1. 100ms users (100ms.ai / 100ms.live email): always allowed across all orgs
 *   2. Non-100ms users: allowed unless their org is in REACT_APP_ANALYTICS_DISABLED_ORG_IDS
 *   3. User must have permission to read org analytics (the only permission gate —
 *      the analytics-only qc-analytics-viewer role grants just this)
 */
export const useShouldShowAnalyticsPage = (contextOverride?: {
  organizationId?: string | null;
  programId?: string | null;
}): { shouldShow: boolean; isLoading: boolean } => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const effectiveOrganizationId =
    contextOverride && 'organizationId' in contextOverride
      ? contextOverride.organizationId
      : selectedOrganizationId;
  const is100msUser = useIs100msUser();
  const { data, isLoading } = usePermissionMap(contextOverride);

  const isOrgDisabled =
    !!effectiveOrganizationId && ANALYTICS_DISABLED_ORG_IDS.has(effectiveOrganizationId);
  const isAnalyticsAllowed = is100msUser || !isOrgDisabled;

  return {
    shouldShow: isAnalyticsAllowed && hasAnalyticsPagePermissions(data?.permissions),
    isLoading,
  };
};
